import React, { useEffect, useState } from "react";
import "./adminUserManagement.css";
import { supabase } from '../../supabase_client.jsx';

export function getApprovedUsers(users) {
  if (!users) return [];
  return users.filter(u => u.is_approved === true && !u.is_reported);
}

export function getPendingUsers(users) {
  if (!users) return [];
  return users.filter(u => u.is_approved !== true && !u.is_reported);
}

export default function AdminUserManagement({ onViewSelect }) {
  const [editUser, setEditUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [userReports, setUserReports] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [banConfirmation, setBanConfirmation] = useState(false);
  const [expandedLists, setExpandedLists] = useState({
    approved: false,
    pending: false,
    reported: false
  });


  const handleApprove = async () => {
    try {
      const { error } = await supabase
        .from("users")
        .update({ is_approved: true })
        .eq("id", editUser.id);

      if (error) {
        alert("Approve failed: " + error.message);
        return;
      }

      setUsers(prev =>
        prev.map(u => u.id === editUser.id ? { ...u, is_approved: true } : u)
      );
      setEditUser(null);
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  const handleUnapprove = async () => {
    try {
      const { error } = await supabase
        .from("users")
        .update({ is_approved: false })
        .eq("id", editUser.id);
      
      if (error) {
        alert("Unapprove failed: " + error.message);
        return;
      }

      setUsers(prev =>
        prev.map(u => u.id === editUser.id ? { ...u, is_approved: false } : u) 
      );
      setEditUser(null);
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("users")
        .delete()
        .eq("id", editUser.id);

      if (error) {
        alert("Delete failed: " + error.message);
        return;
      }

      setUsers(prev => prev.filter(u => u.id !== editUser.id));
      setEditUser(null);
      setDeleteConfirmation(false);
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  // Approve report = flag user as banned, keeping all their data intact
  const handleBanUser = async () => {
    try {
      const { error: banErr } = await supabase
        .from("users")
        .update({
          is_banned: true,
          is_reported: false,
        })
        .eq("id", editUser.id);

      if (banErr) {
        alert("Ban failed: " + banErr.message);
        return;
      }

      // Mark all pending reports as approved
      await supabase
        .from("user_reports")
        .update({ status: "approved", resolved_at: new Date().toISOString() })
        .eq("user_id", editUser.user_id)
        .eq("status", "pending");

      setUsers(prev => prev.filter(u => u.id !== editUser.id));
      setUserReports(prev => prev.filter(r => r.user_id !== editUser.user_id));
      setEditUser(null);
      setBanConfirmation(false);
    } catch (err) {
      console.error("Unexpected ban error:", err);
    }
  };

  // Dismiss report = clear the report, restore user to normal
  const handleDismissReport = async () => {
    try {
      await supabase
        .from("user_reports")
        .update({ status: "dismissed", resolved_at: new Date().toISOString() })
        .eq("user_id", editUser.user_id)
        .eq("status", "pending");

      await supabase
        .from("users")
        .update({ is_reported: false })
        .eq("id", editUser.id);

      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, is_reported: false } : u));
      setUserReports(prev => prev.filter(r => r.user_id !== editUser.user_id));
      setEditUser(null);
    } catch (err) {
      console.error("Unexpected dismiss error:", err);
    }
  };

  async function getUsers() {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("type", "User");

    if (error) {
      console.error("Error fetching users:", error);
    } else {
      setUsers(data);
    }

    // Also fetch all pending reports
    const { data: reports, error: rErr } = await supabase
      .from("user_reports")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (!rErr && reports) {
      setUserReports(reports);
    }
  }

  useEffect(() => {
    getUsers();
  }, []);

  const getApprovedUsers = (users) => {
    if (!users) return [];
    return users.filter(u => u.is_approved === true && !u.is_reported);
  };

  const getPendingUsers = (users) => {
    if (!users) return [];
    return users.filter(u => u.is_approved !== true && !u.is_reported);
  };

  // Returns reported users enriched with their latest report reason
  const getReportedUsers = (users) => {
    if (!users) return [];
    const reportedIds = new Set(userReports.map(r => r.user_id));
    return users
      .filter(u => u.is_reported === true || reportedIds.has(u.user_id))
      .map(u => {
        const latestReport = userReports.find(r => r.user_id === u.user_id);
        return { ...u, _reportReason: latestReport?.reason || null, _reportCount: userReports.filter(r => r.user_id === u.user_id).length };
      });
  };

  const renderUserList = (users, listType) => {

    if (!users || users.length === 0) {
      return <p className="empty-state">No users</p>;
    }

    const isExpanded = expandedLists[listType];
    const visibleUsers = isExpanded ? users : users.slice(0, 5);
    const extraCount = users.length - 5;

    return (
      <div className="event-list-wrapper">
        {visibleUsers.map(user => (
          <div key={user.id} className="user-item" onClick={() => setSelectedUser(user)}>
            <div className="user-text">
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="user-name">{user.username}</span>
                {listType === "reported" && user._reportReason && (
                  <span style={{ fontSize: 12, color: "#b45309", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user._reportCount > 1 && <strong>{user._reportCount} reports · </strong>}
                    {user._reportReason}
                  </span>
                )}
              </div>
            </div>

            <button
              className={`user-action-btn ${listType === "reported" ? "user-action-btn--review" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditUser(user);
              }}
            >
              {listType === "reported" ? "Review" : listType === "pending" ? "Manage" : "Manage"}
            </button>
          </div>
        ))}
        
        {!isExpanded && extraCount > 0 && (
          <div className="expand-toggle" onClick={() => setExpandedLists(prev => ({...prev, [listType]: true}))}>
            + {extraCount} more users
          </div>
        )}
        {isExpanded && extraCount > 0 && (
          <div className="collapse-toggle" onClick={() => setExpandedLists(prev => ({...prev, [listType]: false}))}>
            Collapse
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="user-management-container">
      <div className="home-card">

        {/* header */}
        <div className="calendar-top-nav">
            <div className="nav-left">
                <button className="nav-tab" onClick={() => onViewSelect('admin')}>
                    General
                </button>
                <button className="nav-tab" onClick={() => onViewSelect('adminCalendar')}>
                    Event Calendar
                </button>
                <button className="nav-tab" onClick={() => onViewSelect('adminKPIDash')}>
                  KPI Dashboard
                </button>
                <button className="nav-tab" onClick={() => onViewSelect('adminEventManagement')}>Event Management</button>
                <button className="nav-tab active">
                  User Management
                </button>
                <button className="nav-tab" onClick={() => onViewSelect('business-management')}>
                  Business Management
                </button>
                <button className="bpActionBtn secondary" onClick={() => {
            sessionStorage.removeItem("currentUser");
            sessionStorage.removeItem("currentView");
            onViewSelect("loginSignup");
          }}>
            Log Out
          </button>
            </div>
        </div>
        
        <div className="user-blocks-container">

            {/* approved users */}
            <div className="approved-users user-block">
                <h3>Approved Users</h3>
                {renderUserList(getApprovedUsers(users), 'approved')}
            </div>

            <div className="right-column">
                {/* pending users */}
                <div className="pending-users user-block">
                    <h3>Pending Users</h3>
                    {renderUserList(getPendingUsers(users), 'pending')}
                </div>

                {/* past users */}
                <div className="reported-users user-block">
                    <h3>Reported Users</h3>
                    {renderUserList(getReportedUsers(users), 'reported')}
                </div>
            </div>
        </div>

        {/*user details popup */}
        {selectedUser && (
          <div className="popup-overlay" onClick={() => setSelectedUser(null)}>
            <div className="popup" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>✕</button>

              <div className="popup-header">
                <div className="popup-avatar">{selectedUser.username?.[0]?.toUpperCase()}</div>
                <div>
                  <h2 className="popup-title">{selectedUser.username}</h2>
                  <span className={`popup-badge ${selectedUser.is_approved ? 'badge-approved' : 'badge-pending'}`}>
                    {selectedUser.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>

              <div className="popup-divider" />

              <div className="popup-details">
                {selectedUser.user_id && (
                  <div className="popup-row">
                    <span className="popup-label">User ID</span>
                    <span className="popup-value">{selectedUser.user_id}</span>
                  </div>
                )}
                <div className="popup-row">
                  <span className="popup-label">Reported</span>
                  <span className="popup-value">{selectedUser.is_reported ? 'Yes' : 'No'}</span>
                </div>
                {selectedUser.is_reported && selectedUser._reportCount > 0 && (
                  <div className="popup-row">
                    <span className="popup-label">Report Count</span>
                    <span className="popup-value">{selectedUser._reportCount}</span>
                  </div>
                )}
                {selectedUser.is_reported && selectedUser._reportReason && (
                  <div className="popup-row popup-row-col">
                    <span className="popup-label">Latest Report Reason</span>
                    <span className="popup-value" style={{ textAlign: "left", color: "#b45309", fontSize: 13 }}>{selectedUser._reportReason}</span>
                  </div>
                )}
                {selectedUser.timestamp && (
                  <div className="popup-row">
                    <span className="popup-label">Created</span>
                    <span className="popup-value">{new Date(selectedUser.timestamp).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {editUser && (
          <div className="admin-modal-overlay">
            <div className="admin-modal admin-modal-narrow">
              <button className="admin-close-btn" onClick={() => setEditUser(null)}>✕</button>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "50%",
                  background: editUser.is_reported ? "#fee2e2" : "#111",
                  color: editUser.is_reported ? "#b91c1c" : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, fontWeight: 700, flexShrink: 0
                }}>
                  {editUser.username?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{editUser.username}</div>
                  {editUser.is_reported ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
                      Pending Review
                    </span>
                  ) : (
                    <span className={`popup-badge ${editUser.is_approved ? "badge-approved" : "badge-pending"}`}>
                      {editUser.is_approved ? "Approved" : "Pending"}
                    </span>
                  )}
                </div>
              </div>

              {/* Info grid */}
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                {editUser.user_id && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#6b7280", fontWeight: 500 }}>User ID</span>
                    <span style={{ color: "#111", fontFamily: "monospace", fontSize: 11, maxWidth: 220, textAlign: "right", wordBreak: "break-all" }}>{editUser.user_id}</span>
                  </div>
                )}
                {editUser.timestamp && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#6b7280", fontWeight: 500 }}>Member since</span>
                    <span style={{ color: "#111" }}>{new Date(editUser.timestamp).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                  </div>
                )}
                {editUser.is_reported && editUser._reportCount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#6b7280", fontWeight: 500 }}>Active reports</span>
                    <span style={{ color: "#b91c1c", fontWeight: 700 }}>{editUser._reportCount}</span>
                  </div>
                )}
              </div>

              {/* Report reason card */}
              {editUser.is_reported && editUser._reportReason && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Report Details</div>
                  <div style={{ background: "#fff8f0", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Reported reason</div>
                    <div style={{ fontSize: 14, color: "#111", lineHeight: 1.55 }}>{editUser._reportReason}</div>
                  </div>
                </div>
              )}

              {/* Divider */}
              <div style={{ height: 1, background: "#e5e7eb", marginBottom: 16 }} />

              {/* Footer actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {editUser.is_reported ? (
                  <>
                    <button className="admin-cancel-btn" onClick={() => setEditUser(null)}>Close</button>
                    <button className="admin-unpublish-btn" onClick={handleDismissReport}>Dismiss Report</button>
                    <button className="admin-delete-btn" onClick={() => setBanConfirmation(true)}>Approve & Ban</button>
                  </>
                ) : editUser.is_approved ? (
                  <>
                    <button className="admin-unpublish-btn" onClick={handleUnapprove}>Unapprove</button>
                    <button className="admin-delete-btn" onClick={() => setDeleteConfirmation(true)}>Delete</button>
                  </>
                ) : (
                  <>
                    <button className="admin-approve-btn" onClick={handleApprove}>Approve</button>
                    <button className="admin-delete-btn" onClick={() => setDeleteConfirmation(true)}>Remove</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/** delete */}
        {deleteConfirmation && (
          <div className="admin-modal-overlay">
            <div className="admin-modal admin-delete-confirmation">
              <h2>Confirm {editUser?.is_approved ? 'Delete' : 'Remove'}</h2>
              <p>
                Are you sure you want to {editUser?.is_approved ? 'delete' : 'remove'}{' '}
                <strong>{editUser?.username}</strong>? This cannot be undone.
              </p>
              <div className="admin-modal-footer">
                <button className="admin-cancel-btn" onClick={() => setDeleteConfirmation(false)}>Cancel</button>
                <button className="admin-confirm-delete-btn" onClick={handleDelete}>
                  Yes, {editUser?.is_approved ? 'Delete' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/** ban confirmation */}
        {banConfirmation && (
          <div className="admin-modal-overlay">
            <div className="admin-modal admin-delete-confirmation">
              <h2>Approve Report & Ban User</h2>
              <p>
                This will <strong>ban {editUser?.username}</strong>. Their account data will be wiped
                but their email stays in the system so they can re-register. This cannot be undone.
              </p>
              <div className="admin-modal-footer">
                <button className="admin-cancel-btn" onClick={() => setBanConfirmation(false)}>Cancel</button>
                <button className="admin-confirm-delete-btn" onClick={handleBanUser}>
                  Yes, Ban User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div> 
  );
}